/**
 * Task-status glyph used across delegation rollup, child list, and
 * list-view tree. Rendered as crisp SVG, not unicode.
 *
 * Spec §3.5 (docs/specs/2026-04-16-session-state-audit.md):
 *   ○ todo/backlog   ◑ in_progress   ● done
 *   ⏵ in_review      ⏸ blocked
 */

import { cn } from "@/lib/cn";
import type { TaskStatus } from "@/api/tasks";

export type TaskStatusGlyphSize = "sm" | "md";
const SIZE_PX: Record<TaskStatusGlyphSize, number> = { sm: 10, md: 12 };
// SVG accepts var() in fill/stroke — these resolve to the correct light/dark/clear value.
const COLORS: Record<TaskStatus, string> = {
  done: "var(--color-accent)", // green — brand done state
  in_progress: "var(--color-warning)", // amber — in-flight
  in_review: "var(--color-agent-researcher)", // purple — review / held
  blocked: "var(--color-status-error)", // red — blocked / error
  todo: "var(--color-status-selected)", // blue — selected / planned
  backlog: "var(--color-muted)", // grey — backlog
};

export function TaskStatusGlyph({
  status,
  size = "sm",
  className,
  neutral = false,
}: {
  status: TaskStatus;
  size?: TaskStatusGlyphSize;
  className?: string;
  /** Use the shared muted color while retaining each status shape. */
  neutral?: boolean;
}) {
  const px = SIZE_PX[size];
  const c = neutral ? "var(--color-muted)" : COLORS[status];
  const h = px / 2;
  const r = h - 1;
  const ring = (
    <circle cx={h} cy={h} r={r} fill="none" stroke={c} strokeWidth={1} />
  );
  const glyph =
    status === "done" ? (
      <circle cx={h} cy={h} r={r} fill={c} />
    ) : status === "in_progress" ? (
      <>
        {ring}
        <path
          d={`M ${h} ${h - r} A ${r} ${r} 0 0 1 ${h} ${h + r} Z`}
          fill={c}
        />
      </>
    ) : status === "in_review" ? (
      <path
        d={`M ${h - r / 1.5} ${h - r} L ${h + r} ${h} L ${h - r / 1.5} ${h + r} Z`}
        fill={c}
      />
    ) : status === "blocked" ? (
      <>
        <rect
          x={h - r / 1.3}
          y={h - r}
          width={r / 2.5}
          height={r * 2}
          fill={c}
        />
        <rect x={h + r / 6} y={h - r} width={r / 2.5} height={r * 2} fill={c} />
      </>
    ) : (
      ring
    ); // todo / backlog
  return (
    <svg
      className={cn("shrink-0", className)}
      width={px}
      height={px}
      viewBox={`0 0 ${px} ${px}`}
      aria-label={status}
      role="img"
      data-testid="task-status-glyph"
      data-status={status}
    >
      {glyph}
    </svg>
  );
}
